/**
 * Hook de gerenciamento de questionários.
 *
 * Responsabilidades:
 * - Listar via cache de sessão e excluir via `useAsyncTask`.
 * - Manter estado de carregamento e erro para a UI.
 *
 * Observação:
 * - A UI/Dialogs (preview e confirmação de exclusão) ficam em `components/`.
 * - Este hook não decide a navegação nem manipula JSX.
 */

import { useCallback } from "react";

import { sharedMemoryCache } from "src-next/shared/cache/createMemoryCache";
import { useCachedResource } from "src-next/shared/cache/useCachedResource";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import type { QuestionnairePayload } from "../../form-builder/types";
import { questionnaireManagementService } from "../services";

export interface UseQuestionnaireManagementParams {
  organizationId?: string;
  /**
   * `false` não lista. A chave `crm:questionnaires:{organizationId}` é
   * compartilhada entre o board, o dialog de envio e o formulário de automação.
   */
  enabled?: boolean;
}

const EMPTY_QUESTIONNAIRES: QuestionnairePayload[] = [];

export function questionnairesCacheKey(organizationId: string): string | null {
  const trimmed = organizationId.trim();
  if (!trimmed) return null;
  return `crm:questionnaires:${trimmed}`;
}

export function refreshQuestionnairesCache(
  organizationId: string,
): Promise<QuestionnairePayload[]> {
  const cacheKey = questionnairesCacheKey(organizationId);
  if (!cacheKey) return Promise.resolve(EMPTY_QUESTIONNAIRES);
  return sharedMemoryCache.getOrFetch(
    cacheKey,
    () =>
      questionnaireManagementService.listByOrganizationIdentifier(
        organizationId.trim(),
      ),
    { force: true },
  );
}

export interface UseQuestionnaireManagementResult {
  questionnaires: QuestionnairePayload[];

  isLoading: boolean;
  errorMessage?: string;

  deleteQuestionnaire: (params: { questionnaireId: string }) => Promise<void>;
  isDeleting: boolean;
  deleteErrorMessage?: string;
}

function normalizeErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return "Erro inesperado ao processar a requisição.";
}

export function useQuestionnaireManagement(
  params: UseQuestionnaireManagementParams = {},
): UseQuestionnaireManagementResult {
  const organizationId = params.organizationId?.trim() || "";
  const enabled = (params.enabled ?? true) && Boolean(organizationId);
  const cacheKey = questionnairesCacheKey(organizationId);

  const {
    data: questionnairesData,
    isLoading,
    error: listError,
    refresh,
  } = useCachedResource<QuestionnairePayload[]>({
    cacheKey,
    enabled,
    fetcher: () =>
      questionnaireManagementService.listByOrganizationIdentifier(organizationId),
  });

  const {
    run: runDelete,
    loading: isDeleting,
    error: deleteError,
  } = useAsyncTask<[string], void, string>({
    task: async (questionnaireId) => {
      await questionnaireManagementService.removeById(questionnaireId);
    },
    mapError: normalizeErrorMessage,
  });

  const deleteQuestionnaire = useCallback(
    async ({ questionnaireId }: { questionnaireId: string }) => {
      await runDelete(questionnaireId);
      // Mantém consistência de UI após a deleção.
      await refresh();
    },
    [runDelete, refresh],
  );

  return {
    questionnaires: questionnairesData ?? EMPTY_QUESTIONNAIRES,
    isLoading,
    errorMessage: listError ? normalizeErrorMessage(listError) : undefined,
    deleteQuestionnaire,
    isDeleting,
    deleteErrorMessage: deleteError,
  };
}
